/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { useSelector } from "react-redux"
import { getSubsidies } from "reducers/subsidies"
import { Label, Spacer } from "../atoms/Atoms"
import { RoundedRectangle } from "../atoms/roundedrectangle/RoundedRectangle"
import { CircleImage } from "../atoms/images/CircleImage"
import { BodySmall } from "../atoms/Bodies"
import { AccordionWithoutMarkdown } from "../Accordion"
import styled from "styled-components"
import { useCurrentUser } from "../../context/user/UserContext"
import { DbgPaths } from "../../../utils/Paths"
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl"
import { getCurrencyCode } from "reducers/companyInfo"
import { reimbursementCategoryNameMessages } from "../reimbursements/reimbursementCategoryMessages"
import { Button, Typography } from "@carrotfertility/carotene-core"
import { useHistory } from "react-router-dom"

const optionsKey = "available-funds-module"
const availableFundsOptions = JSON.parse(localStorage.getItem(optionsKey)) || { expanded: {} }

const LabelSpan = styled.span`
  font-family: "Gotham A", "Gotham B", sans-serif;
  font-weight: 450;
  font-size: 11px;
  line-height: 1.5;
  letter-spacing: 0.06em;
`

const AmountSpan = styled.span`
  font-weight: 450;
  font-size: 16px;
`

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const CategoryExpands = styled.div`
  display: flex;
  flex-direction: row;
  margin: 1%;
  width: 90%;
  justify-content: space-between;
`

const CategoryBenefitContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`
const CategorySpacer = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin: 0.5rem 0;
`

const AvailableFundsAmounts = (subsidy) => {
  const isBenefitLifetimeOnly = subsidy.yearlyAmounts.max === subsidy.lifetimeAmounts.max
  const lifetimeAvailable = subsidy.lifetimeAmounts.available
  const yearlyAvailable = subsidy.yearlyAmounts.available
  const hasUsedFunds = subsidy.lifetimeAmounts.claimed > 0
  const currencyCode = useSelector(getCurrencyCode)
  return (
    <CategoryExpands>
      {!isBenefitLifetimeOnly && (
        <CategoryBenefitContainer>
          <div className={"flex flex-row flex-wrap w-50"}>
            <Label>
              <LabelSpan className={"black-60"}>
                {hasUsedFunds ? (
                  <FormattedMessage defaultMessage="Remaining annual" />
                ) : (
                  <FormattedMessage defaultMessage="Available annual" />
                )}
              </LabelSpan>
            </Label>
          </div>
          <Typography variant="h3">
            <AmountSpan>
              <FormattedNumber
                value={Math.max(yearlyAvailable / 100, 0)}
                style="currency"
                currency={currencyCode}
                maximumFractionDigits="0"
                currencyDisplay="narrowSymbol"
              />
            </AmountSpan>
          </Typography>
        </CategoryBenefitContainer>
      )}
      {lifetimeAvailable !== null && (
        <CategoryBenefitContainer>
          <div className={"flex flex-row flex-wrap w-50"}>
            <Label>
              <LabelSpan className={"black-60"}>
                {hasUsedFunds ? (
                  <FormattedMessage defaultMessage="Remaining lifetime" />
                ) : (
                  <FormattedMessage defaultMessage="Available lifetime" />
                )}
              </LabelSpan>
            </Label>
          </div>
          <Typography variant="h3">
            <AmountSpan>
              <FormattedNumber
                value={Math.max(lifetimeAvailable / 100, 0)}
                style="currency"
                currency={currencyCode}
                maximumFractionDigits="0"
                currencyDisplay="narrowSymbol"
              />
            </AmountSpan>
          </Typography>
        </CategoryBenefitContainer>
      )}
    </CategoryExpands>
  )
}

const AvailableFundsByCategory = (subsidy) => {
  const intl = useIntl()
  const { employeeId } = useCurrentUser()
  if (!availableFundsOptions.expanded.hasOwnProperty(employeeId)) {
    availableFundsOptions.expanded[employeeId] = {}
  }
  const optionsForEmployee = availableFundsOptions.expanded[employeeId]
  const categoryKey = subsidy.reimbursementCategoryNameMessageKey.toLowerCase().replaceAll(new RegExp("\\W+", "g"), "-")
  const categoryName = intl.formatMessage(
    reimbursementCategoryNameMessages[subsidy.reimbursementCategoryNameMessageKey]
  )
  const onExpanded = (newState) => {
    optionsForEmployee[categoryKey] = newState
    localStorage.setItem(optionsKey, JSON.stringify(availableFundsOptions))
  }
  return (
    <>
      <Spacer height={1.0} />
      <AccordionWithoutMarkdown
        title={<Typography variant="h5">{categoryName}</Typography>}
        backgroundColor="mint-alternative"
        shouldExpand={optionsForEmployee[categoryKey]}
        chevronOnRight={true}
        chevronHeight={17}
        onClick={onExpanded}
      >
        <Spacer height={0.5} />
        {AvailableFundsAmounts(subsidy)}
      </AccordionWithoutMarkdown>
      <CategorySpacer />
    </>
  )
}

const AvailableFundsModuleCategories = (subsidies) => {
  const hasMultipleCategories = subsidies.length > 1
  return (
    <>
      <CategoriesContainer>
        <div>
          {subsidies.map((subsidy, idx) =>
            hasMultipleCategories ? (
              <React.Fragment key={idx}>{AvailableFundsByCategory(subsidy)}</React.Fragment>
            ) : (
              <React.Fragment key={idx}>{AvailableFundsAmounts(subsidy)}</React.Fragment>
            )
          )}
        </div>
      </CategoriesContainer>
    </>
  )
}

const AvailableFundsModule = ({ appLockedDown = false }) => {
  const subsidies = useSelector(getSubsidies)
  const hasMultipleCategories = subsidies.length > 1
  const history = useHistory()
  const handleMoreDetails = () => {
    history.push(appLockedDown ? DbgPaths.PAYING_FOR_CARE : "/transactions")
  }

  return (
    <RoundedRectangle id="home-sidebar-available-benefit-module" backgroundColor="mint-alternate">
      <CircleImage iconName="illo-coins-and-bills" backgroundColor="mint" />
      <Spacer height={0.5} />
      <Typography variant="h3">
        <FormattedMessage defaultMessage="Carrot funds" />
      </Typography>
      <Spacer height={0.5} />
      <BodySmall color="black">
        {hasMultipleCategories ? (
          <FormattedMessage defaultMessage="Your employer provides funds to cover eligible expenses in the following categories." />
        ) : (
          <FormattedMessage defaultMessage="Your employer provides funds to cover eligible expenses." />
        )}
      </BodySmall>
      <Spacer height={0.5} />
      {subsidies && AvailableFundsModuleCategories(subsidies)}
      <Spacer height={1} />
      <Button variant="outlined" color="secondary" onClick={handleMoreDetails}>
        <span className="nowrap">
          <FormattedMessage defaultMessage="More details" />
        </span>
      </Button>
    </RoundedRectangle>
  )
}

export default AvailableFundsModule
